import { LucideIcon } from 'lucide-react';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type NavItemPosition = 'top' | 'bottom';
export type NavItemVariant = 'default' | 'destructive';

export interface DynamicNavItem {
  key: string;
  label: string;
  headerLabel?: string;
  icon: LucideIcon;
  position: NavItemPosition;
  variant?: NavItemVariant;
  content: ReactNode;
}

interface GlobalSidebarContextValue {
  dynamicItems: DynamicNavItem[];
  registerItem: (item: DynamicNavItem) => void;
  unregisterItem: (key: string) => void;
}

const GlobalSidebarContext = createContext<GlobalSidebarContextValue | null>(
  null,
);

interface GlobalSidebarProviderProps {
  children: ReactNode;
}

export const GlobalSidebarProvider: React.FC<GlobalSidebarProviderProps> = ({
  children,
}) => {
  const [dynamicItems, setDynamicItems] = useState<DynamicNavItem[]>([]);

  const registerItem = useCallback((item: DynamicNavItem) => {
    setDynamicItems((prev) => {
      // Replace if exists, otherwise add
      const filtered = prev.filter((i) => i.key !== item.key);
      return [...filtered, item];
    });
  }, []);

  const unregisterItem = useCallback((key: string) => {
    setDynamicItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const value = useMemo(
    () => ({
      dynamicItems,
      registerItem,
      unregisterItem,
    }),
    [dynamicItems, registerItem, unregisterItem],
  );

  return (
    <GlobalSidebarContext.Provider value={value}>
      {children}
    </GlobalSidebarContext.Provider>
  );
};

export const useGlobalSidebar = (): GlobalSidebarContextValue => {
  const context = useContext(GlobalSidebarContext);
  if (!context) {
    throw new Error(
      'useGlobalSidebar must be used within a GlobalSidebarProvider',
    );
  }
  return context;
};

// Hook to register a sidebar item - automatically unregisters on unmount
export const useSidebarItem = (item: DynamicNavItem | null): void => {
  const { registerItem, unregisterItem } = useGlobalSidebar();

  React.useEffect(() => {
    if (item) {
      registerItem(item);
      return () => unregisterItem(item.key);
    }
    return undefined;
  }, [item, registerItem, unregisterItem]);
};
